import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency, getInitials, roleColors, roleLabels } from '../lib/utils';
import {
  Search, Edit, BarChart3, Link, Copy, Plus, Users, Target, TrendingUp,
  CheckCircle, Clock, Download, UserCheck, Trash2, ShieldCheck,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine } from '../contexts/BusinessLineContext';
import { ImportExportManager } from '../components/ImportExportManager';
import { ColumnCustomizer } from '../components/ColumnCustomizer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ASEGURADORAS = ['Allianz', 'Quálitas', 'Insignia Life', 'Bienes Raíces'];

const ASEGURADORA_COLORS = {
  'Allianz': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'Quálitas': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'Insignia Life': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  'Bienes Raíces': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  // Alias viejo: agentes ya guardados con este string (antes del rename) deben
  // seguir mostrando el mismo color de badge.
  'Flipping Inmobiliario': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};

const SEDE_COLORS = {
  'Guadalajara':      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'Querétaro':        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'Ciudad de México': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};
const SedeBadge = ({ sede }) => sede
  ? <Badge className={`text-[10px] ${SEDE_COLORS[sede] || 'bg-gray-100 text-gray-700'}`}>{sede}</Badge>
  : <span className="text-xs text-muted-foreground">—</span>;

const getVencimientoBadge = (fecha) => {
  if (!fecha) return <span className="text-xs text-muted-foreground">—</span>;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(fecha + 'T00:00:00');
  const daysLeft = Math.floor((d - today) / 86400000);
  if (daysLeft < 0) return <Badge className="text-[10px] bg-red-100 text-red-800">{fecha}</Badge>;
  if (daysLeft <= 30) return <Badge className="text-[10px] bg-orange-100 text-orange-800">{fecha}</Badge>;
  if (daysLeft <= 90) return <Badge className="text-[10px] bg-yellow-100 text-yellow-800">{fecha}</Badge>;
  return <Badge className="text-[10px] bg-green-100 text-green-800">{fecha}</Badge>;
};

const DEFAULT_AGENT_COLUMNS = [
  { id: 'agente',           label: 'Agente',            visible: true,  order: 0 },
  { id: 'contacto',         label: 'Contacto',           visible: true,  order: 1 },
  { id: 'aseguradoras',     label: 'Aseguradora(s)',     visible: true,  order: 2 },
  { id: 'sede',             label: 'Sede',               visible: true,  order: 3 },
  { id: 'link_referidos',   label: 'Link de Referidos',  visible: true,  order: 4 },
  { id: 'clave',            label: 'Clave',              visible: true,  order: 5 },
  { id: 'fecha_alta',          label: 'Fecha Alta Allianz',     visible: true,  order: 6 },
  { id: 'fecha_alta_insignia', label: 'Fecha Alta Insignia',    visible: false, order: 7 },
  { id: 'fecha_alta_qualitas', label: 'Fecha Alta Quálitas',    visible: false, order: 8 },
  { id: 'fecha_nacimiento',    label: 'Fecha Nacimiento',       visible: true,  order: 9 },
  { id: 'direccion',           label: 'Dirección',              visible: false, order: 10 },
  { id: 'cedula_vence',       label: 'Cédula vence',           visible: true,  order: 11, adminOnly: true },
  { id: 'rc_vence',           label: 'RC vence',               visible: true,  order: 12, adminOnly: true },
];

const ASEG_FECHA_ALTA_MAP = {
  'Allianz':       { field: 'fecha_alta',          label: 'Fecha de Alta Allianz' },
  'Insignia Life': { field: 'fecha_alta_insignia', label: 'Fecha de Alta Insignia Life' },
  'Quálitas':      { field: 'fecha_alta_qualitas', label: 'Fecha de Alta Quálitas' },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const AgentsPage = () => {
  const { user: currentUser } = useAuth();
  const { selectedLine, isAllLines } = useBusinessLine();
  const [agents, setAgents] = useState([]);           // raw agent rows
  const [performance, setPerformance] = useState([]); // agent-performance data
  const [referralRequests, setReferralRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('agents');

  // Modals
  const [newAgentOpen, setNewAgentOpen] = useState(false);
  const [editAgentOpen, setEditAgentOpen] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);    // agent row (agents table)
  const [selectedPerf, setSelectedPerf] = useState(null);      // performance row
  const [agentMetrics, setAgentMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [savingAgent, setSavingAgent] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  // Approve modal
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);   // referral request row
  const [approveData, setApproveData] = useState({ role: 'agente', temp_password: '' });
  const [showTempPass, setShowTempPass] = useState(false);

  // Agent import access modal
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [importedAgents, setImportedAgents] = useState([]);    // agents from import result
  const [accessMode, setAccessMode] = useState(null);          // 'all' | 'individual' | null
  const [globalRole, setGlobalRole] = useState('agente');
  const [globalPassword, setGlobalPassword] = useState('');
  const [perAgentConfig, setPerAgentConfig] = useState({});    // {user_id: {grant, role, password}}
  const [grantingAccess, setGrantingAccess] = useState(false);

  // New agent form
  const [newAgentData, setNewAgentData] = useState({
    full_name: '', email: '', phone: '', password: '', aseguradoras: [],
  });

  // Edit agent form
  const [editData, setEditData] = useState({
    full_name: '', email: '', phone: '', aseguradoras: [],
    agent_key: '', fecha_alta: '', fecha_alta_insignia: '', fecha_alta_qualitas: '', fecha_nacimiento: '', direccion: '', sede: null,
    cedula_inicio: '', cedula_vencimiento: '', rc_inicio: '', rc_vencimiento: '',
    claves_agente: [],
  });

  // Delete agent
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState(false);

  // Column customizer
  const [agentColumns, setAgentColumns] = useState(DEFAULT_AGENT_COLUMNS);

  // Bulk selection
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());

  // Bulk action modals
  const [bulkRoleOpen, setBulkRoleOpen] = useState(false);
  const [bulkPasswordOpen, setBulkPasswordOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkRole, setBulkRole] = useState('agente');
  const [bulkPassword, setBulkPassword] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Role sub-tabs inside the "Agentes" outer tab
  const [activeRoleTab, setActiveRoleTab] = useState('agente');
  const [roleAgents, setRoleAgents] = useState({ agente: [], asesor: [], aspirante: [] });
  const [roleLoading, setRoleLoading] = useState(false);

  useEffect(() => { fetchData(); }, [selectedLine]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch role agents when the sub-tab changes
  useEffect(() => { fetchRoleAgents(activeRoleTab); }, [activeRoleTab, selectedLine]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRoleAgents = async (rol) => {
    setRoleLoading(true);
    try {
      const params = new URLSearchParams({ rol });
      if (!isAllLines) params.append('business_line', selectedLine);
      const res = await axios.get(`${API}/agents?${params}`, { headers: authHeaders() });
      setRoleAgents(prev => ({ ...prev, [rol]: res.data }));
    } catch {
      // silently ignore role fetch errors
    } finally {
      setRoleLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const agentsUrl = isAllLines ? `${API}/agents` : `${API}/agents?business_line=${selectedLine}`;
      const [agentsRes, perfRes, reqRes] = await Promise.all([
        axios.get(agentsUrl, { headers: authHeaders() }),
        axios.get(`${API}/reports/agent-performance`, { headers: authHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/referral-requests`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setAgents(agentsRes.data);
      setPerformance(perfRes.data);
      setReferralRequests(reqRes.data);
      // Also refresh the currently active role sub-tab
      fetchRoleAgents(activeRoleTab);
    } catch {
      toast.error('Error al cargar agentes');
    } finally {
      setLoading(false);
    }
  };

  // Merge agents list with performance data for display
  const enrichedAgents = agents.map(agent => {
    const perf = performance.find(p => p.agent?.id === agent.user_id) || {};
    return { ...agent, perf };
  });

  // Flatten for export (ImportExportManager expects flat objects)
  const flatAgentsForExport = agents.map(agent => ({
    full_name:  agent.user?.full_name || '',
    email:      agent.user?.email || '',
    phone:      agent.user?.phone || '',
    agent_key:  agent.agent_key || '',
    aseguradoras: Array.isArray(agent.aseguradoras) ? agent.aseguradoras.join(', ') : '',
    address:    agent.address || '',
    birth_date:          agent.fecha_nacimiento || agent.birth_date || '',
    hire_date:           agent.fecha_alta || '',
    hire_date_insignia:  agent.fecha_alta_insignia || '',
    hire_date_qualitas:  agent.fecha_alta_qualitas || '',
    section:             agent.section || 'activo',
  }));

  const searchLower = search.toLowerCase();
  const filterAgent = a => {
    const name = (a.user?.full_name || a.perf?.agent?.full_name || '').toLowerCase();
    const email = (a.user?.email || a.perf?.agent?.email || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  };

  const filteredAgents  = enrichedAgents.filter(a => (a.section || 'activo') === 'activo' && filterAgent(a));
  const filteredNoveles = enrichedAgents.filter(a => a.section === 'novel' && filterAgent(a));
  const filteredBajas   = enrichedAgents.filter(a => a.section === 'baja'  && filterAgent(a));

  const [sortField, setSortField] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40 inline-block flex-shrink-0" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-[#1a365d] inline-block flex-shrink-0" />
      : <ArrowDown className="h-3 w-3 ml-1 text-[#1a365d] inline-block flex-shrink-0" />;
  };

  const getAgentSortVal = (agent, field) => {
    const userData = agent.user || agent.perf?.agent || {};
    if (field === 'full_name') return (userData.full_name || '').toLowerCase();
    if (field === 'fecha_alta') return agent.fecha_alta || '';
    if (field === 'fecha_nacimiento') return agent.fecha_nacimiento || agent.birth_date || '';
    return (agent[field] ?? '').toString().toLowerCase();
  };

  const sortAgents = (list) => [...list].sort((a, b) => {
    const av = getAgentSortVal(a, sortField);
    const bv = getAgentSortVal(b, sortField);
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const sortedAgents  = sortAgents(filteredAgents);
  const sortedNoveles = sortAgents(filteredNoveles);
  const sortedBajas   = sortAgents(filteredBajas);

  // Role sub-tab agents (enriched with performance data, filtered by search, sorted)
  const enrichedRoleAgents = (roleAgents[activeRoleTab] || []).map(agent => {
    const perf = performance.find(p => p.agent?.id === agent.user_id) || {};
    return { ...agent, perf };
  });
  const sortedRoleAgents = sortAgents(enrichedRoleAgents.filter(filterAgent));

  const visibleAgentCols = [...agentColumns]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .filter(c => c.visible && (!c.adminOnly || ['admin', 'master'].includes(currentUser?.role)));

  const renderClavesCell = (agent) => {
    const claves = agent?.claves_agente;
    if (Array.isArray(claves) && claves.length > 0) {
      if (claves.length === 1) {
        return <span className="text-sm text-muted-foreground">{claves[0]?.clave || '—'}</span>;
      }
      return (
        <span className="text-sm font-medium text-[#1a365d]" title={claves.map(c => c.clave).join(', ')}>
          {claves.length} claves
        </span>
      );
    }
    return <span className="text-sm text-muted-foreground">{agent?.agent_key || '—'}</span>;
  };

  const totalLeads = performance.reduce((s, p) => s + (p.total_leads || 0), 0);
  const totalClients = performance.reduce((s, p) => s + (p.total_clients || 0), 0);
  const avgConversion = performance.length
    ? performance.reduce((s, p) => s + (p.conversion_rate || 0), 0) / performance.length
    : 0;
  const pendingRequests = referralRequests.filter(r => r.status === 'pendiente').length;

  // ── Referral link ──
  const getReferralLink = (agent) => {
    const code = agent?.referral_code || agent?.id?.slice(0, 8) || 'ref';
    return `${window.location.origin}/register?ref=${code}`;
  };

  const copyReferralLink = async (agent) => {
    try {
      await navigator.clipboard.writeText(getReferralLink(agent));
      toast.success('Link copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar el link');
    }
  };

  // ── Open metrics modal ──
  const openMetrics = async (agent, perf) => {
    setSelectedAgent(agent);
    setSelectedPerf(perf);
    setAgentMetrics(null);
    setMetricsOpen(true);
    setMetricsLoading(true);
    const agentUserId = agent?.user_id || perf?.agent?.id;
    try {
      const res = await axios.get(`${API}/agents/metrics/${agentUserId}`, { headers: authHeaders() });
      setAgentMetrics(res.data);
    } catch {
      toast.error('Error al cargar métricas');
    } finally {
      setMetricsLoading(false);
    }
  };

  // ── Open edit modal ──
  const openEdit = (agent, perf) => {
    const userData = agent?.user || perf?.agent || {};
    setSelectedAgent(agent);
    setEditData({
      full_name:          userData.full_name || '',
      email:              userData.email || '',
      phone:              userData.phone || '',
      sede:               userData.sede || null,
      aseguradoras:       agent?.aseguradoras || [],
      agent_key:          agent?.agent_key || '',
      fecha_alta:          agent?.fecha_alta || '',
      fecha_alta_insignia: agent?.fecha_alta_insignia || '',
      fecha_alta_qualitas: agent?.fecha_alta_qualitas || '',
      fecha_nacimiento:    agent?.fecha_nacimiento || agent?.birth_date || '',
      direccion:          agent?.direccion || agent?.address || '',
      cedula_inicio:      userData.cedula_inicio || '',
      cedula_vencimiento: userData.cedula_vencimiento || '',
      rc_inicio:          userData.rc_inicio || '',
      rc_vencimiento:     userData.rc_vencimiento || '',
      claves_agente:      agent?.claves_agente || [],
    });
    setEditAgentOpen(true);
  };

  // ── Open referral modal ──
  const openReferral = (agent, perf) => {
    const merged = { ...agent, ...(agent?.user || perf?.agent || {}) };
    setSelectedAgent(merged);
    setReferralOpen(true);
  };

  // ── Create agent ──
  const handleCreateAgent = async (e) => {
    e.preventDefault();
    if (!newAgentData.aseguradoras.length) {
      toast.error('Selecciona al menos una aseguradora');
      return;
    }
    setSavingAgent(true);
    try {
      await axios.post(`${API}/users`, { ...newAgentData, role: 'agente' }, { headers: authHeaders() });
      toast.success('Agente creado correctamente');
      setNewAgentOpen(false);
      setNewAgentData({ full_name: '', email: '', phone: '', password: '', aseguradoras: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear agente');
    } finally {
      setSavingAgent(false);
    }
  };

  // ── Save edit ──
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editData.aseguradoras.length) {
      toast.error('Selecciona al menos una aseguradora');
      return;
    }
    setSavingAgent(true);
    try {
      const userData = selectedAgent?.user || selectedPerf?.agent || {};
      const userId = userData.id || selectedAgent?.user_id;
      // Update user fields
      await axios.put(`${API}/users/${userId}`, {
        full_name:          editData.full_name,
        email:              editData.email,
        phone:              editData.phone,
        sede:               editData.sede || null,
        cedula_inicio:      editData.cedula_inicio || null,
        cedula_vencimiento: editData.cedula_vencimiento || null,
        rc_inicio:          editData.rc_inicio || null,
        rc_vencimiento:     editData.rc_vencimiento || null,
      }, { headers: authHeaders() });
      // Update agent fields
      if (selectedAgent?.id) {
        await axios.put(`${API}/agents/${selectedAgent.id}`, {
          aseguradoras:     editData.aseguradoras,
          agent_key:        editData.agent_key || null,
          agent_name:       editData.full_name || null,
          fecha_alta:          editData.fecha_alta || null,
          fecha_alta_insignia: editData.fecha_alta_insignia || null,
          fecha_alta_qualitas: editData.fecha_alta_qualitas || null,
          fecha_nacimiento:    editData.fecha_nacimiento || null,
          direccion:        editData.direccion || null,
        }, { headers: authHeaders() });
      }
      toast.success('Agente actualizado');
      setEditAgentOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingAgent(false);
    }
  };

  // ── Delete agent ──
  const handleDeleteAgent = async () => {
    if (!selectedAgent?.id) return;
    setDeletingAgent(true);
    try {
      await axios.delete(`${API}/agents/${selectedAgent.id}`, { headers: authHeaders() });
      toast.success('Agente eliminado correctamente');
      setDeleteConfirmOpen(false);
      setEditAgentOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar agente');
    } finally {
      setDeletingAgent(false);
    }
  };

  // ── Reactivar cuenta suspendida (sin que el agente haya actualizado nada — borra fechas) ──
  const handleReactivar = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/reactivar`, {}, { headers: authHeaders() });
      toast.success('Cuenta reactivada. El agente deberá completar su perfil al próximo login.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reactivar la cuenta');
    }
  };

  // ── Aprobar reactivación (el agente ya actualizó su cédula/RC — conserva las fechas) ──
  const handleApproveReactivation = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/approve-reactivation`, {}, { headers: authHeaders() });
      toast.success('Cuenta reactivada.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aprobar la reactivación');
    }
  };

  // ── Bulk update (role or password) ──
  const handleBulkUpdate = async (type) => {
    const agentIds = [...selectedAgentIds];
    if (!agentIds.length) return;
    if (type === 'password' && !bulkPassword.trim()) {
      toast.error('Ingresa una contraseña');
      return;
    }
    setBulkProcessing(true);
    try {
      const payload = { agent_ids: agentIds };
      if (type === 'role') payload.role = bulkRole;
      if (type === 'password') payload.password = bulkPassword;
      const res = await axios.post(`${API}/agents/bulk-update`, payload, { headers: authHeaders() });
      toast.success(`${res.data.updated} agente(s) actualizados`);
      if (type === 'role') setBulkRoleOpen(false);
      if (type === 'password') setBulkPasswordOpen(false);
      setSelectedAgentIds(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally {
      setBulkProcessing(false);
    }
  };

  // ── Bulk delete ──
  const handleBulkDelete = async () => {
    const agentIds = [...selectedAgentIds];
    if (!agentIds.length) return;
    setBulkProcessing(true);
    try {
      await axios.delete(`${API}/agents/bulk-delete`, {
        data: { agent_ids: agentIds },
        headers: authHeaders(),
      });
      toast.success(`${agentIds.length} agente(s) eliminados`);
      setBulkDeleteConfirmOpen(false);
      setSelectedAgentIds(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setBulkProcessing(false);
    }
  };

  // ── Reject referral request — confirm then DELETE ──
  const handleReject = async (req) => {
    if (!window.confirm(`¿Rechazar solicitud de ${req.full_name}?\nEl candidato podrá volver a registrarse.`)) return;
    try {
      await axios.delete(`${API}/referral-requests/${req.id}`, { headers: authHeaders() });
      toast.success(`Solicitud de ${req.full_name} rechazada`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al rechazar la solicitud');
    }
  };

  // ── Approve referral request — open modal first ──
  const handleApprove = (req) => {
    setApproveTarget(req);
    setApproveData({ role: 'agente', temp_password: '', aseguradora: '' });
    setShowTempPass(false);
    setApproveModalOpen(true);
  };

  const handleConfirmApprove = async (e) => {
    e.preventDefault();
    if (!approveData.temp_password.trim()) {
      toast.error('La contraseña provisional es requerida');
      return;
    }
    setApprovingId(approveTarget.id);
    try {
      const approvePayload = { role: approveData.role, temp_password: approveData.temp_password };
      if (approveData.role === 'aspirante' && approveData.aseguradora) {
        approvePayload.aseguradoras = [approveData.aseguradora];
      }
      await axios.put(
        `${API}/referral-requests/${approveTarget.id}/approve`,
        approvePayload,
        { headers: authHeaders() }
      );
      toast.success(`Cuenta creada para ${approveTarget.full_name}. Se envió correo con credenciales.`);
      setApproveModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aprobar');
    } finally {
      setApprovingId(null);
    }
  };

  // ── Export metrics to Excel ──
  const exportMetricsExcel = () => {
    if (!agentMetrics) return;
    const userData = selectedAgent?.user || selectedPerf?.agent || selectedAgent || {};
    const name = userData.full_name || 'Agente';
    const wb = XLSX.utils.book_new();
    // Summary sheet
    const summary = [
      ['Agente', name],
      ['Leads totales', agentMetrics.total_leads],
      ['Clientes totales', agentMetrics.total_clients],
      ['Pólizas cerradas', agentMetrics.total_policies],
      ['Tasa de conversión', `${agentMetrics.conversion_rate}%`],
      ['Comisiones generadas', agentMetrics.total_commissions],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, ws1, 'Resumen');
    // Monthly activity sheet
    const monthlyRows = [['Mes', 'Leads', 'Clientes'], ...(agentMetrics.monthly_activity || []).map(m => [m.month, m.leads, m.clients])];
    const ws2 = XLSX.utils.aoa_to_sheet(monthlyRows);
    XLSX.utils.book_append_sheet(wb, ws2, 'Actividad Mensual');
    XLSX.writeFile(wb, `metricas-${name.replace(/\s+/g, '-')}.xlsx`);
  };

  // ── After agent import: show access modal ──
  const handleAgentImportSuccess = (importResult) => {
    fetchData();
    const agents = importResult?.agents || [];
    // Filter to agents that don't already have access
    const newAgents = agents.filter(a => !a.has_access);
    if (newAgents.length === 0) {
      toast.success('Agentes importados. Ya tenían acceso a la plataforma.');
      return;
    }
    setImportedAgents(newAgents);
    const initialConfig = {};
    newAgents.forEach(a => {
      initialConfig[a.user_id] = { grant: true, role: 'agente', password: '' };
    });
    setPerAgentConfig(initialConfig);
    setAccessMode(null);
    setGlobalRole('agente');
    setGlobalPassword('');
    setAccessModalOpen(true);
  };

  // ── Grant access to imported agents ──
  const handleGrantAccess = async () => {
    let agentsToGrant = [];
    if (accessMode === 'all') {
      if (!globalPassword.trim()) {
        toast.error('La contraseña provisional es requerida');
        return;
      }
      agentsToGrant = importedAgents.map(a => ({
        user_id: a.user_id,
        role: globalRole,
        password: globalPassword,
      }));
    } else if (accessMode === 'individual') {
      agentsToGrant = importedAgents
        .filter(a => perAgentConfig[a.user_id]?.grant)
        .map(a => ({
          user_id: a.user_id,
          role: perAgentConfig[a.user_id]?.role || 'agente',
          password: perAgentConfig[a.user_id]?.password || '',
        }))
        .filter(a => a.password.trim());
      if (agentsToGrant.length === 0) {
        toast.error('Ingresa una contraseña para al menos un agente');
        return;
      }
    }
    setGrantingAccess(true);
    try {
      const res = await axios.post(
        `${API}/agents/bulk-access`,
        { agents: agentsToGrant },
        { headers: authHeaders() }
      );
      toast.success(`Acceso otorgado a ${res.data.updated} agente(s). Deberán cambiar su contraseña en el primer inicio.`);
      setAccessModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al otorgar acceso');
    } finally {
      setGrantingAccess(false);
    }
  };

  // ── Toggle aseguradora checkbox ──
  const toggleAseguradora = (list, setList, item) => {
    if (list.includes(item)) {
      setList(list.filter(a => a !== item));
    } else {
      setList([...list, item]);
    }
  };

  const AseguradoraCheckboxes = ({ value, onChange }) => (
    <div className="grid grid-cols-2 gap-2 mt-1">
      {ASEGURADORAS.map(a => (
        <label key={a} className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={value.includes(a)}
            onChange={() => onChange(a)}
            className="w-4 h-4 rounded border-gray-300"
          />
          <span className="text-sm">{a}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="agents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Agentes</h1>
          <p className="text-muted-foreground">Gestión y rendimiento del equipo</p>
        </div>
        <div className="flex gap-2">
          <ImportExportManager
            module="agents"
            apiUrl={API}
            data={flatAgentsForExport}
            onSuccess={handleAgentImportSuccess}
          />
          <Button
            className="bg-[#2d8659] hover:bg-[#2d8659]/90"
            onClick={() => setNewAgentOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Agente
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Agentes</p>
              <p className="text-2xl font-bold">{agents.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-[#1a365d] text-white"><Users className="h-5 w-5" /></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Leads</p>
              <p className="text-2xl font-bold">{totalLeads}</p>
            </div>
            <div className="p-3 rounded-lg bg-[#2d8659] text-white"><Target className="h-5 w-5" /></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Clientes</p>
              <p className="text-2xl font-bold">{totalClients}</p>
            </div>
            <div className="p-3 rounded-lg bg-[#6b7280] text-white"><Users className="h-5 w-5" /></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Conversión Promedio</p>
              <p className="text-2xl font-bold">{avgConversion.toFixed(1)}%</p>
            </div>
            <div className="p-3 rounded-lg bg-[#1a365d] text-white"><TrendingUp className="h-5 w-5" /></div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk action bar */}
      {selectedAgentIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-[#1a365d]/10 border border-[#1a365d]/20 rounded-lg">
          <span className="text-sm font-medium text-[#1a365d] dark:text-blue-300">
            {selectedAgentIds.size} agente(s) seleccionado(s)
          </span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={() => { setBulkRole('agente'); setBulkRoleOpen(true); }}>
              Cambiar rol
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setBulkPassword(''); setBulkPasswordOpen(true); }}>
              Contraseña temporal
            </Button>
            {['admin', 'master'].includes(currentUser?.role) && (
              <Button size="sm" variant="destructive" onClick={() => setBulkDeleteConfirmOpen(true)}>
                <Trash2 className="h-3 w-3 mr-1" />
                Eliminar ({selectedAgentIds.size})
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelectedAgentIds(new Set())}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="agents">
            Agentes
            {(roleAgents.agente.length + roleAgents.asesor.length + roleAgents.aspirante.length) > 0 && (
              <span className="ml-1.5 bg-[#1a365d] text-white text-[10px] rounded-full px-1.5 py-0.5">{roleAgents.agente.length + roleAgents.asesor.length + roleAgents.aspirante.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="noveles">
            Noveles
            {enrichedAgents.filter(a => a.section === 'novel').length > 0 && (
              <span className="ml-1.5 bg-blue-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{enrichedAgents.filter(a => a.section === 'novel').length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="bajas">
            Bajas
            {enrichedAgents.filter(a => a.section === 'baja').length > 0 && (
              <span className="ml-1.5 bg-gray-400 text-white text-[10px] rounded-full px-1.5 py-0.5">{enrichedAgents.filter(a => a.section === 'baja').length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="requests" className="relative">
            Solicitudes de Registro
            {pendingRequests > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">{pendingRequests}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Agents Tab ── */}
        <TabsContent value="agents" className="space-y-4">
          {/* Role sub-tabs — secondary underline style, NOT a Tabs component */}
          <div className="flex gap-0 border-b border-border">
            {[
              { value: 'agente',    label: 'Agentes',    count: roleAgents.agente.length,    dot: 'bg-[#1a365d]' },
              { value: 'asesor',    label: 'Asesores',   count: roleAgents.asesor.length,    dot: 'bg-green-600' },
              { value: 'aspirante', label: 'Aspirantes', count: roleAgents.aspirante.length, dot: 'bg-violet-600' },
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setActiveRoleTab(tab.value)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                  activeRoleTab === tab.value
                    ? 'border-[#1a365d] text-[#1a365d] dark:text-blue-400 dark:border-blue-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`${tab.dot} text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none`}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          <Card>
            <CardContent className="p-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={`Buscar ${activeRoleTab === 'agente' ? 'agentes' : activeRoleTab === 'asesor' ? 'asesores' : 'aspirantes'}...`}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <ColumnCustomizer
                  columns={agentColumns}
                  storageKey="agents"
                  onColumnsChange={setAgentColumns}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        checked={sortedRoleAgents.length > 0 && sortedRoleAgents.every(a => selectedAgentIds.has(a.id))}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedAgentIds(prev => new Set([...prev, ...sortedRoleAgents.map(a => a.id)]));
                          } else {
                            setSelectedAgentIds(prev => {
                              const next = new Set(prev);
                              sortedRoleAgents.forEach(a => next.delete(a.id));
                              return next;
                            });
                          }
                        }}
                      />
                    </TableHead>
                    {visibleAgentCols.map(col => {
                      if (col.id === 'agente') return <TableHead key="agente" className="cursor-pointer select-none" onClick={() => handleSort('full_name')}>Agente <SortIcon field="full_name" /></TableHead>;
                      if (col.id === 'contacto') return <TableHead key="contacto">Contacto</TableHead>;
                      if (col.id === 'aseguradoras') return <TableHead key="aseguradoras">Aseguradora(s)</TableHead>;
                      if (col.id === 'sede') return <TableHead key="sede">Sede</TableHead>;
                      if (col.id === 'link_referidos') return <TableHead key="link_referidos">Link de Referidos</TableHead>;
                      if (col.id === 'clave') return <TableHead key="clave">Clave</TableHead>;
                      if (col.id === 'fecha_alta') return <TableHead key="fecha_alta" className="cursor-pointer select-none" onClick={() => handleSort('fecha_alta')}>Fecha de Alta <SortIcon field="fecha_alta" /></TableHead>;
                      if (col.id === 'fecha_alta_insignia') return <TableHead key="fecha_alta_insignia">Fecha Alta Insignia</TableHead>;
                      if (col.id === 'fecha_alta_qualitas') return <TableHead key="fecha_alta_qualitas">Fecha Alta Quálitas</TableHead>;
                      if (col.id === 'fecha_nacimiento') return <TableHead key="fecha_nacimiento" className="cursor-pointer select-none" onClick={() => handleSort('fecha_nacimiento')}>Fecha Nacimiento <SortIcon field="fecha_nacimiento" /></TableHead>;
                      if (col.id === 'direccion') return <TableHead key="direccion">Dirección</TableHead>;
                      if (col.id === 'cedula_vence') return <TableHead key="cedula_vence">Cédula vence</TableHead>;
                      if (col.id === 'rc_vence') return <TableHead key="rc_vence">RC vence</TableHead>;
                      return null;
                    })}
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(loading || roleLoading) ? (
                    <TableRow>
                      <TableCell colSpan={agentColumns.filter(c => c.visible).length + 2} className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d] mx-auto"></div>
                      </TableCell>
                    </TableRow>
                  ) : sortedRoleAgents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={agentColumns.filter(c => c.visible).length + 2} className="text-center py-8 text-muted-foreground">
                        No se encontraron {activeRoleTab === 'agente' ? 'agentes' : activeRoleTab === 'asesor' ? 'asesores' : 'aspirantes'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRoleAgents.map((agent) => {
                      const userData = agent.user || agent.perf?.agent || {};
                      const aseguradoras = agent.aseguradoras || [];
                      const rowKey = agent.id || agent.user_id;
                      return (
                        <TableRow key={rowKey} className={selectedAgentIds.has(agent.id) ? 'bg-[#1a365d]/5' : ''}>
                          <TableCell>
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                              checked={selectedAgentIds.has(agent.id)}
                              onChange={e => {
                                setSelectedAgentIds(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(agent.id);
                                  else next.delete(agent.id);
                                  return next;
                                });
                              }}
                            />
                          </TableCell>
                          {visibleAgentCols.map(col => {
                            if (col.id === 'agente') return (
                              <TableCell key="agente">
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-9 w-9">
                                    <AvatarImage src={userData.avatar_url} />
                                    <AvatarFallback className="bg-[#1a365d] text-white text-xs">
                                      {getInitials(userData.full_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-medium">{userData.full_name || '—'}</div>
                                    <Badge className={`${roleColors[userData.role] || ''} text-[10px] h-4`}>
                                      {roleLabels[userData.role] || userData.role || 'agente'}
                                    </Badge>
                                  </div>
                                </div>
                              </TableCell>
                            );
                            if (col.id === 'contacto') return (
                              <TableCell key="contacto">
                                <div className="text-sm">{userData.email || '—'}</div>
                                <div className="text-xs text-muted-foreground">{userData.phone || '—'}</div>
                              </TableCell>
                            );
                            if (col.id === 'aseguradoras') return (
                              <TableCell key="aseguradoras">
                                <div className="flex flex-wrap gap-1">
                                  {aseguradoras.length > 0
                                    ? aseguradoras.map(a => (
                                        <Badge key={a} className={`text-[10px] ${ASEGURADORA_COLORS[a] || 'bg-gray-100 text-gray-700'}`}>
                                          {a}
                                        </Badge>
                                      ))
                                    : <span className="text-xs text-muted-foreground">—</span>
                                  }
                                </div>
                              </TableCell>
                            );
                            if (col.id === 'sede') return (
                              <TableCell key="sede">
                                <SedeBadge sede={userData.sede} />
                              </TableCell>
                            );
                            if (col.id === 'link_referidos') return (
                              <TableCell key="link_referidos">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1.5 text-xs"
                                  onClick={() => copyReferralLink(agent)}
                                >
                                  <Link className="h-3 w-3" />
                                  <Copy className="h-3 w-3" />
                                  Copiar link
                                </Button>
                              </TableCell>
                            );
                            if (col.id === 'clave') return <TableCell key="clave">{renderClavesCell(agent)}</TableCell>;
                            if (col.id === 'fecha_alta') {
                              const fechaAltaVal = selectedLine === 'insignia'
                                ? (agent.fecha_alta_insignia || '—')
                                : selectedLine === 'qualitas'
                                  ? (agent.fecha_alta_qualitas || '—')
                                  : (agent.fecha_alta || '—');
                              return <TableCell key="fecha_alta" className="text-sm text-muted-foreground">{fechaAltaVal}</TableCell>;
                            }
                            if (col.id === 'fecha_alta_insignia') return <TableCell key="fecha_alta_insignia" className="text-sm text-muted-foreground">{agent.fecha_alta_insignia || '—'}</TableCell>;
                            if (col.id === 'fecha_alta_qualitas') return <TableCell key="fecha_alta_qualitas" className="text-sm text-muted-foreground">{agent.fecha_alta_qualitas || '—'}</TableCell>;
                            if (col.id === 'fecha_nacimiento') return <TableCell key="fecha_nacimiento" className="text-sm text-muted-foreground">{agent.fecha_nacimiento || agent.birth_date || '—'}</TableCell>;
                            if (col.id === 'direccion') return <TableCell key="direccion" className="text-sm text-muted-foreground max-w-[150px] truncate">{agent.direccion || '—'}</TableCell>;
                            if (col.id === 'cedula_vence') return <TableCell key="cedula_vence">{getVencimientoBadge(userData.cedula_vencimiento)}</TableCell>;
                            if (col.id === 'rc_vence') return <TableCell key="rc_vence">{getVencimientoBadge(userData.rc_vencimiento)}</TableCell>;
                            return null;
                          })}
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {userData.pending_review && (
                                <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 border-0">
                                  Pendiente de revisión
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="gap-1 text-xs"
                                onClick={() => openMetrics(agent, agent.perf)}
                                title="Ver métricas"
                              >
                                <BarChart3 className="h-4 w-4" />
                                <span className="hidden sm:inline">Métricas</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="gap-1 text-xs"
                                onClick={() => openEdit(agent, agent.perf)}
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                                <span className="hidden sm:inline">Editar</span>
                              </Button>
                              {['admin', 'master'].includes(currentUser?.role) && userData.account_suspended && !userData.pending_review && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleReactivar(userData.id)}
                                  title="Reactivar cuenta"
                                >
                                  <ShieldCheck className="h-4 w-4" />
                                  <span className="hidden sm:inline">Reactivar</span>
                                </Button>
                              )}
                              {['admin', 'master'].includes(currentUser?.role) && userData.pending_review && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleApproveReactivation(userData.id)}
                                  title="Aprobar reactivación"
                                >
                                  <UserCheck className="h-4 w-4" />
                                  <span className="hidden sm:inline">Aprobar</span>
                                </Button>
                              )}
                              {['admin', 'master'].includes(currentUser?.role) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => { setSelectedAgent(agent); setDeleteConfirmOpen(true); }}
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  <span className="hidden sm:inline">Eliminar</span>
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Noveles Tab ── */}
        <TabsContent value="noveles" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agentes Noveles</CardTitle>
              <CardDescription>Agentes en período de formación importados desde Excel</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        checked={sortedNoveles.length > 0 && sortedNoveles.every(a => selectedAgentIds.has(a.id))}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedAgentIds(prev => new Set([...prev, ...sortedNoveles.map(a => a.id)]));
                          } else {
                            setSelectedAgentIds(prev => {
                              const next = new Set(prev);
                              sortedNoveles.forEach(a => next.delete(a.id));
                              return next;
                            });
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Agente</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Aseguradora(s)</TableHead>
                    <TableHead>Clave</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedNoveles.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No hay agentes noveles</TableCell></TableRow>
                  ) : sortedNoveles.map(agent => {
                    const userData = agent.user || agent.perf?.agent || {};
                    const aseguradoras = agent.aseguradoras || [];
                    return (
                      <TableRow key={agent.id} className={selectedAgentIds.has(agent.id) ? 'bg-[#1a365d]/5' : ''}>
                        <TableCell>
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                            checked={selectedAgentIds.has(agent.id)}
                            onChange={e => {
                              setSelectedAgentIds(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(agent.id);
                                else next.delete(agent.id);
                                return next;
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={userData.avatar_url} />
                              <AvatarFallback className="bg-blue-600 text-white text-xs">{getInitials(userData.full_name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">{userData.full_name || '—'}</div>
                              <Badge className="bg-blue-100 text-blue-800 text-[10px] h-4">Novel</Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{userData.email || '—'}</div>
                          <div className="text-xs text-muted-foreground">{userData.phone || '—'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {aseguradoras.length > 0 ? aseguradoras.map(a => (
                              <Badge key={a} className={`text-[10px] ${ASEGURADORA_COLORS[a] || 'bg-gray-100 text-gray-700'}`}>{a}</Badge>
                            )) : <span className="text-xs text-muted-foreground">—</span>}
                          </div>
                        </TableCell>
                        <TableCell>{renderClavesCell(agent)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => openMetrics(agent, agent.perf)}>
                              <BarChart3 className="h-4 w-4" /><span className="hidden sm:inline">Métricas</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => openEdit(agent, agent.perf)}>
                              <Edit className="h-4 w-4" /><span className="hidden sm:inline">Editar</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Bajas Tab ── */}
        <TabsContent value="bajas" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agentes con Baja</CardTitle>
              <CardDescription>Agentes que han dado de baja importados desde Excel</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        checked={sortedBajas.length > 0 && sortedBajas.every(a => selectedAgentIds.has(a.id))}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedAgentIds(prev => new Set([...prev, ...sortedBajas.map(a => a.id)]));
                          } else {
                            setSelectedAgentIds(prev => {
                              const next = new Set(prev);
                              sortedBajas.forEach(a => next.delete(a.id));
                              return next;
                            });
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Agente</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Aseguradora(s)</TableHead>
                    <TableHead>Clave</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedBajas.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No hay agentes con baja</TableCell></TableRow>
                  ) : sortedBajas.map(agent => {
                    const userData = agent.user || agent.perf?.agent || {};
                    const aseguradoras = agent.aseguradoras || [];
                    return (
                      <TableRow key={agent.id} className={`opacity-60 ${selectedAgentIds.has(agent.id) ? 'bg-[#1a365d]/5 !opacity-100' : ''}`}>
                        <TableCell>
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                            checked={selectedAgentIds.has(agent.id)}
                            onChange={e => {
                              setSelectedAgentIds(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(agent.id);
                                else next.delete(agent.id);
                                return next;
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={userData.avatar_url} />
                              <AvatarFallback className="bg-gray-500 text-white text-xs">{getInitials(userData.full_name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">{userData.full_name || '—'}</div>
                              <Badge className="bg-gray-100 text-gray-600 text-[10px] h-4">Baja</Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{userData.email || '—'}</div>
                          <div className="text-xs text-muted-foreground">{userData.phone || '—'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {aseguradoras.length > 0 ? aseguradoras.map(a => (
                              <Badge key={a} className={`text-[10px] ${ASEGURADORA_COLORS[a] || 'bg-gray-100 text-gray-700'}`}>{a}</Badge>
                            )) : <span className="text-xs text-muted-foreground">—</span>}
                          </div>
                        </TableCell>
                        <TableCell>{renderClavesCell(agent)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => openEdit(agent, agent.perf)}>
                            <Edit className="h-4 w-4" /><span className="hidden sm:inline">Editar</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Referral Requests Tab ── */}
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Solicitudes de Registro</CardTitle>
              <CardDescription>Candidatos que solicitaron unirse mediante link de referido</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Aseguradoras</TableHead>
                    <TableHead>Fecha Nac.</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No hay solicitudes de registro
                      </TableCell>
                    </TableRow>
                  ) : (
                    referralRequests.map(req => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.full_name}</TableCell>
                        <TableCell>{req.email}</TableCell>
                        <TableCell>{req.phone || '—'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {req.aseguradora_otra && (
                              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs">
                                ⚠ Verificar aseguradora
                              </Badge>
                            )}
                            {(req.aseguradoras || []).filter(a => a !== 'Otra').map(a => (
                              <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                            ))}
                            {(!req.aseguradoras || req.aseguradoras.length === 0) && !req.aseguradora_otra && (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {req.birth_date || '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground capitalize">
                          {req.rol || '—'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              req.status === 'aprobado' ? 'bg-green-100 text-green-800' :
                              req.status === 'rechazado' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }
                          >
                            {req.status === 'pendiente' ? <Clock className="h-3 w-3 mr-1 inline" /> :
                             req.status === 'aprobado' ? <CheckCircle className="h-3 w-3 mr-1 inline" /> : null}
                            {req.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {req.created_at ? new Date(req.created_at).toLocaleDateString('es-MX') : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {req.status === 'pendiente' && (
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                className="bg-[#2d8659] hover:bg-[#2d8659]/90 text-xs gap-1"
                                onClick={() => handleApprove(req)}
                                disabled={approvingId === req.id}
                              >
                                {approvingId === req.id
                                  ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                  : <UserCheck className="h-3 w-3" />}
                                Aprobar
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs gap-1"
                                onClick={() => handleReject(req)}
                                disabled={approvingId === req.id}
                              >
                                <Trash2 className="h-3 w-3" />
                                Rechazar
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Modal: Nuevo Agente ── */}
      <Dialog open={newAgentOpen} onOpenChange={setNewAgentOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Agente</DialogTitle>
            <DialogDescription>Registra un nuevo agente en el sistema</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAgent} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo *</Label>
              <Input
                value={newAgentData.full_name}
                onChange={e => setNewAgentData({ ...newAgentData, full_name: e.target.value })}
                placeholder="Nombre completo"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newAgentData.email}
                onChange={e => setNewAgentData({ ...newAgentData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={newAgentData.phone}
                onChange={e => setNewAgentData({ ...newAgentData, phone: e.target.value })}
                placeholder="+52 55 1234 5678"
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña *</Label>
              <Input
                type="password"
                value={newAgentData.password}
                onChange={e => setNewAgentData({ ...newAgentData, password: e.target.value })}
                placeholder="••••••••"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Aseguradora(s) *</Label>
              <div className="border rounded-lg p-3">
                <AseguradoraCheckboxes
                  value={newAgentData.aseguradoras}
                  onChange={item => setNewAgentData(prev => ({
                    ...prev,
                    aseguradoras: prev.aseguradoras.includes(item)
                      ? prev.aseguradoras.filter(a => a !== item)
                      : [...prev.aseguradoras, item],
                  }))}
                />
              </div>
              {newAgentData.aseguradoras.length === 0 && (
                <p className="text-xs text-muted-foreground">Selecciona al menos una aseguradora</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setNewAgentOpen(false)} disabled={savingAgent}>Cancelar</Button>
              <Button type="submit" className="bg-[#1a365d] hover:bg-[#1a365d]/90" disabled={savingAgent}>
                {savingAgent && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                Crear Agente
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Editar Agente ── */}
      <Dialog open={editAgentOpen} onOpenChange={setEditAgentOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Agente</DialogTitle>
            <DialogDescription>Modifica los datos del agente</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            {/* Datos de contacto */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Nombre completo *</Label>
                <Input
                  value={editData.full_name}
                  onChange={e => setEditData({ ...editData, full_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editData.email}
                  onChange={e => setEditData({ ...editData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={editData.phone}
                  onChange={e => setEditData({ ...editData, phone: e.target.value })}
                />
              </div>
            </div>

            {/* Claves por aseguradora/sede (importadas) */}
            {editData.claves_agente?.length > 0 && (
              <div className="space-y-2">
                <Label>Claves registradas</Label>
                <div className="border rounded-lg divide-y text-sm">
                  {editData.claves_agente.map((c, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2">
                      <span className="font-mono font-medium">{c.clave || '—'}</span>
                      {c.aseguradora && <span className="text-xs text-muted-foreground">· {c.aseguradora}</span>}
                      {c.sede && <span className="text-xs text-muted-foreground">· {c.sede}</span>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Las claves múltiples se agregan al importar agentes.</p>
              </div>
            )}

            {/* Datos del expediente */}
            <div className="grid grid-cols-2 gap-3">
              {selectedAgent?.user?.role !== 'aspirante' && (
                <div className="space-y-2">
                  <Label>{editData.claves_agente?.length > 0 ? 'Clave principal (legacy)' : 'Clave (agente_key)'}</Label>
                  <Input
                    value={editData.agent_key}
                    onChange={e => setEditData({ ...editData, agent_key: e.target.value })}
                    placeholder="Ej. ALZ-001"
                  />
                </div>
              )}
              {(() => {
                const aseguradoras = editData.aseguradoras || [];
                const mapped = aseguradoras.map(a => ASEG_FECHA_ALTA_MAP[a]).filter(Boolean);
                const entries = mapped.length > 0 ? mapped : [ASEG_FECHA_ALTA_MAP['Allianz']];
                const showSpecific = entries.length > 1;
                return entries.map(({ field, label }) => (
                  <div key={field} className="space-y-2">
                    <Label>{showSpecific ? label : 'Fecha de Alta'}</Label>
                    <Input
                      type="date"
                      value={editData[field] || ''}
                      onChange={e => setEditData({ ...editData, [field]: e.target.value })}
                    />
                  </div>
                ));
              })()}
              <div className="space-y-2">
                <Label>Fecha de Nacimiento</Label>
                <Input
                  type="date"
                  value={editData.fecha_nacimiento}
                  onChange={e => setEditData({ ...editData, fecha_nacimiento: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  value={editData.direccion}
                  onChange={e => setEditData({ ...editData, direccion: e.target.value })}
                  placeholder="Calle, colonia, ciudad"
                />
              </div>
              <div className="space-y-2">
                <Label>Sede</Label>
                <Select
                  value={editData.sede || ''}
                  onValueChange={v => setEditData({ ...editData, sede: v || null })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin sede" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Guadalajara">Guadalajara</SelectItem>
                    <SelectItem value="Querétaro">Querétaro</SelectItem>
                    <SelectItem value="Ciudad de México">Ciudad de México</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Documentos profesionales — solo admin */}
            {['admin', 'master'].includes(currentUser?.role) && (
              <div className="border rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Documentos profesionales</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Cédula — Inicio</Label>
                    <Input
                      type="date"
                      value={editData.cedula_inicio}
                      onChange={e => setEditData({ ...editData, cedula_inicio: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Cédula — Vencimiento</Label>
                    <Input
                      type="date"
                      value={editData.cedula_vencimiento}
                      onChange={e => setEditData({ ...editData, cedula_vencimiento: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Póliza RC — Inicio</Label>
                    <Input
                      type="date"
                      value={editData.rc_inicio}
                      onChange={e => setEditData({ ...editData, rc_inicio: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Póliza RC — Vencimiento</Label>
                    <Input
                      type="date"
                      value={editData.rc_vencimiento}
                      onChange={e => setEditData({ ...editData, rc_vencimiento: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Aseguradoras */}
            <div className="space-y-2">
              <Label>Aseguradora(s) *</Label>
              <div className="border rounded-lg p-3">
                <AseguradoraCheckboxes
                  value={editData.aseguradoras}
                  onChange={item => setEditData(prev => ({
                    ...prev,
                    aseguradoras: prev.aseguradoras.includes(item)
                      ? prev.aseguradoras.filter(a => a !== item)
                      : [...prev.aseguradoras, item],
                  }))}
                />
              </div>
            </div>

            {/* Botones */}
            <div className="flex items-center justify-between gap-2 pt-2">
              {['admin', 'master'].includes(currentUser?.role) && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={savingAgent}
                >
                  Eliminar agente
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => setEditAgentOpen(false)} disabled={savingAgent}>Cancelar</Button>
                <Button type="submit" className="bg-[#1a365d] hover:bg-[#1a365d]/90" disabled={savingAgent}>
                  {savingAgent && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                  Guardar
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Confirmar eliminación de agente ── */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">¿Eliminar agente?</DialogTitle>
            <DialogDescription>
              Esta acción eliminará al agente{' '}
              <strong>{selectedAgent?.user?.full_name || selectedAgent?.agent_name || 'seleccionado'}</strong>{' '}
              y su usuario asociado permanentemente. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={deletingAgent}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAgent}
              disabled={deletingAgent}
            >
              {deletingAgent && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              Sí, eliminar permanentemente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Link de Referidos ── */}
      <Dialog open={referralOpen} onOpenChange={setReferralOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link de Referidos</DialogTitle>
            <DialogDescription>
              Link único de registro para{' '}
              <strong>{selectedAgent?.full_name || selectedAgent?.user?.full_name || 'este agente'}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-mono break-all">
                {selectedAgent && getReferralLink(selectedAgent)}
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => selectedAgent && copyReferralLink(selectedAgent)}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copiar Link
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Este link abre un formulario público de registro. Las solicitudes recibidas aparecen en la pestaña "Solicitudes de Registro" y requieren aprobación del admin.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Métricas ── */}
      <Dialog open={metricsOpen} onOpenChange={setMetricsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Métricas — {selectedAgent?.user?.full_name || selectedPerf?.agent?.full_name || 'Agente'}
            </DialogTitle>
            <DialogDescription>Resumen de desempeño del agente</DialogDescription>
          </DialogHeader>

          {metricsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a365d]"></div>
            </div>
          ) : agentMetrics ? (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Leads asignados', value: agentMetrics.total_leads, color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' },
                  { label: 'Clientes', value: agentMetrics.total_clients, color: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' },
                  { label: 'Pólizas cerradas', value: agentMetrics.total_policies, color: 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300' },
                  { label: 'Tasa de conversión', value: `${agentMetrics.conversion_rate}%`, color: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300' },
                  { label: 'Comisiones generadas', value: formatCurrency(agentMetrics.total_commissions), color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300', colSpan: true },
                ].map(kpi => (
                  <div key={kpi.label} className={`rounded-lg p-4 ${kpi.color} ${kpi.colSpan ? 'col-span-2 sm:col-span-1' : ''}`}>
                    <p className="text-xs font-medium opacity-75">{kpi.label}</p>
                    <p className="text-2xl font-bold mt-1">{kpi.value}</p>
                  </div>
                ))}
              </div>

              {/* Monthly activity chart */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Actividad mensual (últimos 6 meses)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={agentMetrics.monthly_activity} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="leads" name="Leads" fill="#1a365d" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="clients" name="Clientes" fill="#2d8659" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Export button */}
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={exportMetricsExcel} className="gap-2">
                  <Download className="h-4 w-4" />
                  Exportar Excel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No se pudieron cargar las métricas.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Modal: Aprobar Solicitud ── */}
      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Aprobar solicitud</DialogTitle>
            <DialogDescription>
              Configura el acceso para <strong>{approveTarget?.full_name}</strong> antes de crear su cuenta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfirmApprove} className="space-y-4">
            <div className="space-y-2">
              <Label>Rol *</Label>
              <Select
                value={approveData.role}
                onValueChange={v => setApproveData(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agente">Agente</SelectItem>
                  <SelectItem value="aspirante">Aspirante</SelectItem>
                  <SelectItem value="asesor">Asesor</SelectItem>
                  <SelectItem value="viewer">Viewer (solo lectura)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {approveData.role === 'aspirante' && (
              <div className="space-y-2">
                <Label>Aseguradora *</Label>
                <select
                  value={approveData.aseguradora}
                  onChange={e => setApproveData(prev => ({ ...prev, aseguradora: e.target.value }))}
                  required
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">Seleccionar aseguradora</option>
                  <option value="Allianz">Allianz</option>
                  <option value="Insignia Life">Insignia Life</option>
                  <option value="Qualitas">Quálitas</option>
                </select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Contraseña provisional *</Label>
              <div className="relative">
                <Input
                  type={showTempPass ? 'text' : 'password'}
                  value={approveData.temp_password}
                  onChange={e => setApproveData(prev => ({ ...prev, temp_password: e.target.value }))}
                  placeholder="Contraseña para primer acceso"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowTempPass(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showTempPass ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Se le enviará un correo a {approveTarget?.email} con esta contraseña.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setApproveModalOpen(false)}
                disabled={!!approvingId}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#2d8659] hover:bg-[#2d8659]/90"
                disabled={!!approvingId}
              >
                {approvingId && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                Aprobar y crear cuenta
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Bulk cambiar rol ── */}
      <Dialog open={bulkRoleOpen} onOpenChange={setBulkRoleOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cambiar rol</DialogTitle>
            <DialogDescription>
              Asigna un nuevo rol a los {selectedAgentIds.size} agente(s) seleccionados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nuevo rol</Label>
              <Select value={bulkRole} onValueChange={setBulkRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agente">Agente</SelectItem>
                  <SelectItem value="aspirante">Aspirante</SelectItem>
                  <SelectItem value="asesor">Asesor</SelectItem>
                  <SelectItem value="viewer">Viewer (solo lectura)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkRoleOpen(false)} disabled={bulkProcessing}>Cancelar</Button>
            <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90" onClick={() => handleBulkUpdate('role')} disabled={bulkProcessing}>
              {bulkProcessing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
              Aplicar a {selectedAgentIds.size} agente(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Bulk contraseña temporal ── */}
      <Dialog open={bulkPasswordOpen} onOpenChange={setBulkPasswordOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Contraseña temporal</DialogTitle>
            <DialogDescription>
              Asigna una contraseña provisional a los {selectedAgentIds.size} agente(s) seleccionados. Deberán cambiarla en su próximo inicio de sesión.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nueva contraseña provisional *</Label>
              <Input
                type="password"
                value={bulkPassword}
                onChange={e => setBulkPassword(e.target.value)}
                placeholder="Contraseña temporal"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkPasswordOpen(false)} disabled={bulkProcessing}>Cancelar</Button>
            <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90" onClick={() => handleBulkUpdate('password')} disabled={bulkProcessing}>
              {bulkProcessing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
              Aplicar a {selectedAgentIds.size} agente(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Bulk eliminar confirmación ── */}
      <Dialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">¿Eliminar {selectedAgentIds.size} agente(s)?</DialogTitle>
            <DialogDescription>
              Esta acción eliminará permanentemente los agentes seleccionados y sus usuarios asociados. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setBulkDeleteConfirmOpen(false)} disabled={bulkProcessing}>Cancelar</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkProcessing}>
              {bulkProcessing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
              Sí, eliminar {selectedAgentIds.size} agente(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Acceso a plataforma tras import ── */}
      <Dialog open={accessModalOpen} onOpenChange={setAccessModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>¿Dar acceso a la plataforma a estos agentes?</DialogTitle>
            <DialogDescription>
              Se importaron {importedAgents.length} agente(s) sin acceso al CRM. Elige cómo configurar sus cuentas.
            </DialogDescription>
          </DialogHeader>

          {/* Option selector */}
          {!accessMode && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-2">
              <button
                onClick={() => setAccessMode('all')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-transparent bg-green-50 dark:bg-green-900/20 hover:border-green-500 text-green-800 dark:text-green-300 transition-all text-left"
              >
                <CheckCircle className="h-7 w-7" />
                <span className="font-semibold text-sm">Sí a todos</span>
                <span className="text-xs opacity-75 text-center">Un rol y contraseña provisional para todos</span>
              </button>
              <button
                onClick={() => { setAccessModalOpen(false); toast.info('Agentes importados sin acceso a la plataforma.'); }}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-800 hover:border-gray-400 text-gray-700 dark:text-gray-300 transition-all text-left"
              >
                <UserCheck className="h-7 w-7" />
                <span className="font-semibold text-sm">No a ninguno</span>
                <span className="text-xs opacity-75 text-center">Solo crea los agentes sin acceso al CRM</span>
              </button>
              <button
                onClick={() => setAccessMode('individual')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-transparent bg-blue-50 dark:bg-blue-900/20 hover:border-blue-500 text-blue-800 dark:text-blue-300 transition-all text-left"
              >
                <Users className="h-7 w-7" />
                <span className="font-semibold text-sm">Configurar uno por uno</span>
                <span className="text-xs opacity-75 text-center">Elige acceso, rol y contraseña por agente</span>
              </button>
            </div>
          )}

          {/* Sí a todos */}
          {accessMode === 'all' && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-1">Agentes que recibirán acceso ({importedAgents.length}):</p>
                <ul className="space-y-0.5 text-muted-foreground max-h-32 overflow-y-auto">
                  {importedAgents.map(a => (
                    <li key={a.user_id} className="flex gap-2">
                      <span>{a.full_name}</span>
                      <span className="opacity-60">— {a.email}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Rol *</Label>
                  <Select value={globalRole} onValueChange={setGlobalRole}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agente">Agente</SelectItem>
                      <SelectItem value="aspirante">Aspirante</SelectItem>
                      <SelectItem value="asesor">Asesor</SelectItem>
                      <SelectItem value="viewer">Viewer (solo lectura)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Contraseña provisional *</Label>
                  <Input
                    type="password"
                    value={globalPassword}
                    onChange={e => setGlobalPassword(e.target.value)}
                    placeholder="Contraseña temporal"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Los agentes deberán cambiar su contraseña en el primer inicio de sesión.
              </p>
              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setAccessMode(null)}>Cambiar opción</Button>
                <Button
                  className="bg-[#2d8659] hover:bg-[#2d8659]/90"
                  onClick={handleGrantAccess}
                  disabled={grantingAccess}
                >
                  {grantingAccess && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                  Confirmar y dar acceso
                </Button>
              </div>
            </div>
          )}

          {/* Configurar uno por uno */}
          {accessMode === 'individual' && (
            <div className="space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">Acceso</TableHead>
                      <TableHead>Agente</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Contraseña provisional</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importedAgents.map(a => {
                      const cfg = perAgentConfig[a.user_id] || { grant: true, role: 'agente', password: '' };
                      return (
                        <TableRow key={a.user_id} className={!cfg.grant ? 'opacity-40' : ''}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={!!cfg.grant}
                              onChange={e => setPerAgentConfig(prev => ({
                                ...prev,
                                [a.user_id]: { ...prev[a.user_id], grant: e.target.checked },
                              }))}
                              className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">{a.full_name}</div>
                            <div className="text-xs text-muted-foreground">{a.email}</div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={cfg.role}
                              onValueChange={v => setPerAgentConfig(prev => ({
                                ...prev,
                                [a.user_id]: { ...prev[a.user_id], role: v },
                              }))}
                              disabled={!cfg.grant}
                            >
                              <SelectTrigger className="h-8 text-xs w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="agente">Agente</SelectItem>
                                <SelectItem value="aspirante">Aspirante</SelectItem>
                                <SelectItem value="asesor">Asesor</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="password"
                              placeholder="Contraseña temporal"
                              value={cfg.password}
                              onChange={e => setPerAgentConfig(prev => ({
                                ...prev,
                                [a.user_id]: { ...prev[a.user_id], password: e.target.value },
                              }))}
                              disabled={!cfg.grant}
                              className="h-8 text-xs w-44"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">
                Solo los agentes con el toggle activo y contraseña ingresada recibirán acceso.
              </p>
              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setAccessMode(null)}>Cambiar opción</Button>
                <Button
                  className="bg-[#2d8659] hover:bg-[#2d8659]/90"
                  onClick={handleGrantAccess}
                  disabled={grantingAccess}
                >
                  {grantingAccess && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                  Confirmar accesos
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
